import { Container, Divider, Typography, makeStyles } from '@material-ui/core';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import Link from '../../components/link';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';
import Seo from '../../components/seo';
import FeatureBlock from './_feature-block';

const useStyles = makeStyles(theme => ({
  featureHeading: {
    marginBottom: theme.spacing(1),
  },
}));

const ServiceDiscoveryPage: React.FC = () => {
  const classes = useStyles();

  const title = 'Service discovery';
  const description =
    'Automatically instrument load balancing and service discovery with every deployment';

  return (
    <PageLayout>
      <Seo title={title} description={description} />

      <Section variant="hero-green">
        <Typography variant="h1" align="center">
          {title}
        </Typography>
        <Typography variant="subtitle1" align="center">
          {description}
        </Typography>
      </Section>

      <FeatureBlock
        image={<StaticImage src="../../images/icons/lg/service-mesh.svg" alt="Service mesh" />}
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Service mesh without the hassle
        </Typography>
        <Typography variant="body1" paragraph>
          Service meshes are all the rage when it comes to cloud-native application management,
          monitoring, and security, but it takes a lot of time and expertise to set them up and take
          full advantage of them.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect gives you all the perks of a service mesh without any of the headaches.{' '}
          <Link to="//consul.io">Consul</Link> is automatically deployed when you register your
          cluster, and each deployment communicates directly with the mesh to ensure services are
          discovered correctly.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={<StaticImage src="../../images/icons/lg/api-gateway.svg" alt="API gateway" />}
      >
        <Typography variant="h3" className={classes.featureHeading}>
          API gateways everywhere
        </Typography>
        <Typography variant="body1" paragraph>
          Service meshes are great for inter-process communication, but what about apps and services
          that need to be exposed to the outside world?
        </Typography>
        <Typography variant="body1" paragraph>
          Just like with the service mesh, Architect deploys a powerful API gateway,{' '}
          <Link to="//traefik.io/">Traefik</Link>, into each registered cluster and continuously
          configures it with each deployment to ensure apps and services are accessible at your
          preferred addresses.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        image={<StaticImage src="../../images/icons/lg/dns-zones.svg" alt="DNS zones" />}
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Custom DNS zones
        </Typography>
        <Typography variant="body1">
          Every environment deployed by Architect is allocated a unique DNS zone to fulfill external
          API requests, but a production environment isn't complete without your company's domain
          name. Architect allows you to register custom DNS zones with each environment and
          simplifies the process of exposing apps and services through your own domain.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={
          <StaticImage
            src="../../images/icons/lg/any-cloud-provider.svg"
            alt="Depdency injection"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Dependency injection
        </Typography>
        <Typography variant="body1" paragraph>
          Applications are more than their containers. They depend on the ability to connect to
          external dependencies like databases, messaging queues, peer APIs, and more.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect's built-in dependency resolver not only ensures that these external dependencies
          are provisioned, but goes even further to inject the locations of these dependencies into
          consuming applications. This injection means applications need less configuration in order
          to be deployed and minimizes any potential human error.
        </Typography>
      </FeatureBlock>
    </PageLayout>
  );
};

export default ServiceDiscoveryPage;
